import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  useOAuthTokens,
  type OAuthTokens,
  type User,
} from "@privy-io/react-auth";
import { toast } from "sonner";
import { useSubmitShareXOAuthToken } from "@/lib/hooks/useApi";
import { PrivyXOAuthTokenContext } from "@/lib/providers/PrivyXOAuthTokenContext";

const getTwitterAccount = (user: User) => {
  return user.twitter;
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return fallback;
  }

  const err = error as {
    response?: {
      data?: {
        message?: string;
        error?: string;
      };
    };
  };

  return err.response?.data?.message ?? err.response?.data?.error ?? fallback;
};

export const PrivyXOAuthTokenProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [lastTokenHandoffError, setLastTokenHandoffError] =
    useState<unknown>(null);
  const { mutateAsync: submitShareXOAuthToken, isPending: isSubmittingToken } =
    useSubmitShareXOAuthToken();

  const submitTokenGrant = useCallback(
    async ({ oAuthTokens, user }: { oAuthTokens: OAuthTokens; user: User }) => {
      if (oAuthTokens.provider !== "twitter") return;

      try {
        setLastTokenHandoffError(null);
        const twitter = getTwitterAccount(user);
        await submitShareXOAuthToken({
          provider: "twitter",
          accessToken: oAuthTokens.accessToken,
          accessTokenExpiresInSeconds: oAuthTokens.accessTokenExpiresInSeconds,
          refreshToken: oAuthTokens.refreshToken,
          refreshTokenExpiresInSeconds:
            oAuthTokens.refreshTokenExpiresInSeconds,
          scopes: oAuthTokens.scopes,
          xHandle: twitter?.username ?? null,
          xUserId: twitter?.subject ?? null,
        });
        toast.success("X authorized", {
          id: "x-oauth-token-stored",
          position: "top-center",
        });
      } catch (error) {
        setLastTokenHandoffError(error);
        toast.error("X authorization failed", {
          id: "x-oauth-token-failed",
          description: getApiErrorMessage(error, "Please authorize X again."),
          position: "top-center",
        });
      }
    },
    [submitShareXOAuthToken],
  );

  const { reauthorize } = useOAuthTokens({
    onOAuthTokenGrant: (params) => {
      void submitTokenGrant(params);
    },
  });

  const reauthorizeX = useCallback(async () => {
    setLastTokenHandoffError(null);
    await reauthorize({ provider: "twitter" });
  }, [reauthorize]);

  const value = useMemo(
    () => ({
      reauthorizeX,
      isSubmittingToken,
      lastTokenHandoffError,
    }),
    [isSubmittingToken, lastTokenHandoffError, reauthorizeX],
  );

  return (
    <PrivyXOAuthTokenContext.Provider value={value}>
      {children}
    </PrivyXOAuthTokenContext.Provider>
  );
};
